import { useState } from 'react'
import { useSubscription } from '../../hooks/useSubscription'
import { supabase } from '../../lib/supabase'
import { Button } from '../../components/ui/Button'
import { Spinner } from '../../components/ui/Spinner'
import { cn } from '../../lib/utils'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

const PAID_FEATURES = [
  'Unlimited members',
  'All features',
  'Budget & bills',
  'Health logs',
  'Vault',
  'Linked households',
]

const BILLING_OPTIONS = [
  {
    id:       'monthly',
    name:     'Monthly',
    price:    '$4',
    cycle:    '/mo',
    desc:     'Full access, billed monthly.',
    features: PAID_FEATURES,
    badge:    '7-day trial',
  },
  {
    id:       'annual',
    name:     'Annual',
    price:    '$40',
    cycle:    '/yr',
    desc:     'Full access, billed annually.',
    features: PAID_FEATURES,
    badge:    '7-day trial · Save 17%',
  },
  {
    id:       'lifetime',
    name:     'Lifetime',
    price:    '$120',
    cycle:    'one-time',
    desc:     'Pay once, yours forever.',
    features: [...PAID_FEATURES, 'All future updates', 'Priority support'],
    badge:    'One-time payment',
  },
]

const FREE_FEATURES = ['Up to 5 members', 'Calendar & tasks', 'Shopping lists', 'Meal planning']

const PlanFeatures = ({ features }) => (
  <ul className="mt-2 space-y-1">
    {features.map((f) => (
      <li key={f} className="flex items-center gap-2 text-xs text-text-secondary">
        <svg className="text-emerald-500 flex-shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
        {f}
      </li>
    ))}
  </ul>
)

function getPlanLabel(sub) {
  if (!sub || sub.plan === 'free') return 'Free'
  if (sub.status === 'lifetime' || sub.billing_cycle === 'lifetime') return 'Paid (Lifetime)'
  if (sub.billing_cycle === 'annual') return 'Paid (Annual)'
  return 'Paid (Monthly)'
}

// Which billing option card is "current" (to highlight it)
function getCurrentOptionId(sub) {
  if (!sub || sub.plan === 'free') return null
  if (sub.status === 'lifetime' || sub.billing_cycle === 'lifetime') return 'lifetime'
  if (sub.billing_cycle === 'annual') return 'annual'
  return 'monthly'
}

export const BillingPage = () => {
  const { data: sub, isLoading: subLoading } = useSubscription()
  const [loading,      setLoading]      = useState(null)  // option id being loaded
  const [error,        setError]        = useState('')
  const [showCancel,   setShowCancel]   = useState(false)
  const [cancelling,   setCancelling]   = useState(false)

  const currentStatus    = sub?.status ?? 'active'
  const isLifetimePlan   = currentStatus === 'lifetime' || sub?.billing_cycle === 'lifetime'
  const currentOptionId  = getCurrentOptionId(sub)
  const isPaid           = sub?.plan === 'paid'

  const callCheckout = async (action, extraBody = {}) => {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout`, {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey':        SUPABASE_KEY,
      },
      body: JSON.stringify({ action, ...extraBody }),
    })
    return res.json()
  }

  const handleSelect = async (option) => {
    if (option.id === currentOptionId) return
    setError('')
    setLoading(option.id)
    try {
      const body = await callCheckout('checkout', { billing_cycle: option.id })
      if (body.url) { window.location.href = body.url; return }
      setError('Could not start checkout. Please try again.')
    } catch {
      setError('Network error. Please check your connection.')
    }
    setLoading(null)
  }

  const handleSwitchToFree = async () => {
    setError('')
    setLoading('free')
    try {
      const body = await callCheckout('cancel')
      if (body.ok) { window.location.reload(); return }
      setError(body.error ?? 'Could not switch plan. Please try again.')
    } catch {
      setError('Network error. Please check your connection.')
    }
    setLoading(null)
  }

  const handlePortal = async () => {
    setError('')
    setLoading('portal')
    try {
      const body = await callCheckout('portal')
      if (body.url) { window.location.href = body.url; return }
      setError('Could not open billing portal. Please try again.')
    } catch {
      setError('Network error. Please check your connection.')
    }
    setLoading(null)
  }

  const handleCancel = async () => {
    setCancelling(true)
    try {
      const body = await callCheckout('cancel')
      if (body.ok) { setShowCancel(false); window.location.reload(); return }
      setError(body.error ?? 'Could not cancel subscription.')
    } catch {
      setError('Network error. Please check your connection.')
    }
    setCancelling(false)
  }

  if (subLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Billing</h1>
        <p className="text-sm text-text-muted mt-1">Manage your plan and payment details.</p>
      </div>

      {/* Current plan banner */}
      <div className="rounded-base bg-primary-light border border-primary/20 px-4 py-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-primary uppercase tracking-wide">Current plan</p>
          <p className="text-lg font-bold text-text-primary mt-0.5">{getPlanLabel(sub)}</p>
          {sub?.current_period_end && !isLifetimePlan && (
            <p className="text-xs text-text-muted mt-0.5">
              {currentStatus === 'canceled' ? 'Access until' : 'Renews'}{' '}
              {new Date(sub.current_period_end).toLocaleDateString(undefined, {
                month: 'long', day: 'numeric', year: 'numeric',
              })}
            </p>
          )}
          {currentStatus === 'trialing' && (
            <span className="inline-block mt-1 text-[11px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
              Free trial active
            </span>
          )}
          {currentStatus === 'past_due' && (
            <span className="inline-block mt-1 text-[11px] font-semibold bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">
              Payment past due
            </span>
          )}
          {sub?.is_comped && (
            <span className="inline-block mt-1 text-[11px] font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
              Comped account
            </span>
          )}
        </div>
        {isPaid && !isLifetimePlan && !sub?.is_comped && (
          <Button variant="secondary" size="sm" onClick={handlePortal} disabled={!!loading}>
            {loading === 'portal' ? <Spinner size="sm" /> : 'Manage payment'}
          </Button>
        )}
      </div>

      {/* Free plan card */}
      <div
        className={cn(
          'rounded-base border-2 p-4 transition-all',
          !isPaid ? 'border-primary bg-primary-light' : 'border-border bg-surface'
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className={cn('font-semibold text-sm', !isPaid ? 'text-primary' : 'text-text-primary')}>
                Free
              </span>
              {!isPaid && (
                <span className="text-[10px] font-semibold bg-primary text-white px-1.5 py-0.5 rounded-full">
                  Current
                </span>
              )}
            </div>
            <p className="text-xs text-text-muted mt-0.5">Core household tools for one family.</p>
            <PlanFeatures features={FREE_FEATURES} />
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <span className={cn('font-bold text-lg', !isPaid ? 'text-primary' : 'text-text-primary')}>$0</span>
            {isPaid && currentStatus !== 'canceled' && (
              <Button
                variant="secondary"
                size="sm"
                disabled={!!loading || isLifetimePlan || !!sub?.is_comped}
                onClick={() => setShowCancel(true)}
              >
                {loading === 'free' ? <Spinner size="sm" /> : 'Switch to free'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Paid billing option cards */}
      <div className="space-y-3">
        {BILLING_OPTIONS.map((option) => {
          const isCurrent = option.id === currentOptionId

          return (
            <div
              key={option.id}
              className={cn(
                'rounded-base border-2 p-4 transition-all',
                isCurrent ? 'border-primary bg-primary-light' : 'border-border bg-surface'
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn('font-semibold text-sm', isCurrent ? 'text-primary' : 'text-text-primary')}>
                      {option.name}
                    </span>
                    {isCurrent && (
                      <span className="text-[10px] font-semibold bg-primary text-white px-1.5 py-0.5 rounded-full">
                        Current
                      </span>
                    )}
                    <span className={cn(
                      'text-[10px] font-semibold px-1.5 py-0.5 rounded-full',
                      option.id === 'lifetime'
                        ? 'bg-violet-100 text-violet-700'
                        : 'bg-amber-100 text-amber-700'
                    )}>
                      {option.badge}
                    </span>
                  </div>
                  <p className="text-xs text-text-muted mt-0.5">{option.desc}</p>
                  <PlanFeatures features={option.features} />
                </div>

                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <div className="text-right">
                    <span className={cn('font-bold text-lg', isCurrent ? 'text-primary' : 'text-text-primary')}>
                      {option.price}
                    </span>
                    <span className="text-xs text-text-muted">{option.cycle}</span>
                  </div>

                  {!isCurrent && (
                    <Button
                      size="sm"
                      disabled={!!loading || isLifetimePlan || !!sub?.is_comped}
                      onClick={() => handleSelect(option)}
                    >
                      {loading === option.id ? <Spinner size="sm" /> : 'Select'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {error && (
        <div className="rounded-base bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Cancel subscription confirmation */}
      {isPaid && !isLifetimePlan && currentStatus !== 'canceled' && !sub?.is_comped && (
        <div className="pt-2 border-t border-border">
          {showCancel ? (
            <div className="rounded-base bg-rose-50 border border-rose-200 p-4 space-y-3">
              <p className="text-sm font-medium text-rose-700">Cancel subscription?</p>
              <p className="text-xs text-rose-600">
                You'll keep access until the end of your billing period. This can't be undone.
              </p>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => setShowCancel(false)} disabled={cancelling}>
                  Keep plan
                </Button>
                <Button
                  size="sm"
                  className="bg-rose-600 hover:bg-rose-700 text-white border-rose-600"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? <Spinner size="sm" /> : 'Yes, cancel'}
                </Button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowCancel(true)}
              className="text-sm text-text-muted hover:text-rose-600 transition-colors"
            >
              Cancel subscription
            </button>
          )}
        </div>
      )}
    </div>
  )
}
